import type { Feature, FeatureCollection, Polygon, MultiPolygon, LineString } from "geojson";
import type { MapTool } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { parsePolygonId, geoJSONKeyForType } from "../../helpers/JobMap/editHelpers";
import { cutPolygonUseCase } from "../../../application/map/useCases/cutPolygonUseCase";
import type { FeatureId } from "../engine/useMapDrawEngine";

export function makeCutTool(deps: ToolDeps): MapTool {
	let jobId: string | null = null;
	let targetPolygon: Feature<Polygon> | null = null;
	let typePrefix: string | null = null;

	function onFinish(_id: FeatureId, _ctx: { mode: string; action: string }) {
		if (!jobId || !targetPolygon || !typePrefix) return;

		const snapshot = deps.drawEngine.getSnapshot();
		const lineFeature = snapshot.find((f) => f.geometry.type === "LineString");
		if (!lineFeature) return;

		const cuttingLine = (lineFeature.geometry as LineString).coordinates;
		const applyFC = typePrefix === "boundary" ? deps.applyBoundary : deps.applyZones;

		const result = cutPolygonUseCase(
			{ polygon: targetPolygon, cuttingLine },
			(resultGeom) => {
				const fc: FeatureCollection = {
					type: "FeatureCollection",
					features: [resultGeom],
				};
				applyFC(jobId!, fc);
			},
			(original) => {
				const fc: FeatureCollection = {
					type: "FeatureCollection",
					features: [original],
				};
				applyFC(jobId!, fc);
			},
		);

		if (!result.ok) {
			deps.showError(result.error);
			return;
		}

		deps.undoStack.push(result.data.command);
		deps.drawEngine.clear();
	}

	return {
		id: "cut",
		label: "Cut",
		cursor: "crosshair",
		onActivate() {
			const polygonId = deps.getSelectedPolygonId();
			if (!polygonId) {
				deps.showError("Select a polygon to cut");
				deps.deactivateTool();
				return;
			}

			const parsed = parsePolygonId(polygonId);
			if (!parsed) {
				deps.showError("Invalid polygon ID");
				deps.deactivateTool();
				return;
			}

			jobId = deps.getSelectedJobId();
			if (!jobId) {
				deps.showError("No job selected");
				deps.deactivateTool();
				return;
			}

			const geoKey = geoJSONKeyForType(parsed.typePrefix);
			const fc =
				geoKey === "boundaryGeoJSON"
					? deps.getBoundaryFC(jobId)
					: geoKey === "zonesGeoJSON"
						? deps.getZonesFC(jobId)
						: null;

			if (!fc) {
				deps.showError("Geometry data not found");
				deps.deactivateTool();
				return;
			}

			const feature = fc.features[parsed.featureIndex] as Feature<Polygon | MultiPolygon> | undefined;
			if (!feature || feature.geometry.type !== "Polygon") {
				deps.showError("Can only cut Polygon features");
				deps.deactivateTool();
				return;
			}

			targetPolygon = feature as Feature<Polygon>;
			typePrefix = parsed.typePrefix;

			deps.drawEngine.setMode("linestring");
			deps.drawEngine.setFinishHandler(onFinish);
		},
		onDeactivate() {
			deps.drawEngine.setFinishHandler(null);
			deps.drawEngine.clear();
			deps.drawEngine.setMode("static");
			jobId = null;
			targetPolygon = null;
			typePrefix = null;
		},
	};
}
